<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useAuthStore } from './stores/auth'
import { useCustomerStore } from './stores/customer'
import { useStaffSessionStore } from './stores/staffSession'
import { pwaConfig } from './utils/pwaTools'
import { authReady } from './firebase'
import {
  initCustomerRoutePrefetchOnTabVisible,
  prefetchCustomerShell,
  resetCustomerRoutePrefetchState,
} from '@/utils/routePrefetch'
import Toast from './components/Toast.vue'
import UTMCapture from './components/UTMCapture.vue'
import DevToolsManager from './components/DevToolsManager.vue'
import VitePreloadErrorHandler from './components/VitePreloadErrorHandler.vue'
import NotSupported from './views/NotSupported.vue'
import PullToReloadNative from './components/PullToReloadNative.vue'
import SigningOutOverlay from './components/SigningOutOverlay.vue'
import { showOpenInBrowserTipOncePerSession } from '@/utils/inAppBrowser'
import { bootstrapBufferedBookingTrackingFlush } from '@/components/consultation/bookingTracking/bookingTrackingService'
import { notifySwAuthSync } from '@/utils/swAuthSync'
import { reportSwActivatedMessage } from '@/utils/swLifecycleTelemetry'

const authStore = useAuthStore()
const customerStore = useCustomerStore()
const staffSessionStore = useStaffSessionStore()

let openInBrowserTipTimer: number | undefined

function handleServiceWorkerMessage(event: MessageEvent) {
  if (event.data?.type === 'SW_ACTIVATED') {
    reportSwActivatedMessage()
    const currentUser = authStore.user
    const issuedAtMs = authStore.tokenIssuedAtTime?.getTime()
    if (currentUser && issuedAtMs != null) {
      void notifySwAuthSync({ uid: currentUser.uid, issuedAtMs })
    } else {
      void notifySwAuthSync()
    }
  }
}

watch(
  () =>
    [authStore.isLoading, authStore.isAuthenticated, authStore.isCustomer] as [
      boolean,
      boolean,
      boolean | null,
    ],
  ([loading, authed, customer]) => {
    if (loading || !authed || customer !== true) {
      resetCustomerRoutePrefetchState()
      return
    }
    prefetchCustomerShell()
    initCustomerRoutePrefetchOnTabVisible()
    bootstrapBufferedBookingTrackingFlush()
  },
  { flush: 'post' },
)

onMounted(async () => {
  // Initialize auth store if not already initialized
  await authReady
  authStore.init()

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
  }

  openInBrowserTipTimer = window.setTimeout(() => {
    openInBrowserTipTimer = undefined
    showOpenInBrowserTipOncePerSession()
  }, 2500)
})

onUnmounted(() => {
  resetCustomerRoutePrefetchState()
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
  }
  if (openInBrowserTipTimer !== undefined) {
    clearTimeout(openInBrowserTipTimer)
    openInBrowserTipTimer = undefined
  }
  authStore.onUnmounted()
  customerStore.onUnmounted()
  staffSessionStore.onUnmounted()
})

const bgColorClass =
  import.meta.env.VITE_ENVIRONMENT_COLOR === 'yellow'
    ? 'yellow-bg-color'
    : import.meta.env.VITE_ENVIRONMENT_COLOR === 'green'
      ? 'green-bg-color'
      : import.meta.env.VITE_ENVIRONMENT_COLOR === 'violet'
        ? 'violet-bg-color'
        : ''
</script>

<template>
  <PullToReloadNative>
    <div id="app-container" :class="{ 'app-container': true, [bgColorClass]: true }">
      <!-- UTM Capture Component - captures UTM parameters on app load -->

      <!-- Main Content Area -->
      <main class="flex-1 w-full">
        <!-- Loading State -->
        <div v-if="authStore.isLoading" class="flex items-center justify-center min-h-[100dvh]">
          <div class="text-center">
            <div class="loading loading-spinner loading-lg mb-4"></div>
            <div class="text-xl font-semibold text-base-content">Loading...</div>
          </div>
        </div>

        <SigningOutOverlay v-else-if="authStore.isSigningOut" />

        <!-- Not Supported Page (PWA disabled but running in standalone) -->
        <NotSupported v-else-if="pwaConfig.isDisabled && pwaConfig.runningInStandaloneMode" />

        <!-- Main Content -->
        <router-view v-else />
      </main>
      <!-- Toast Notifications -->
      <Toast />
      <DevToolsManager />
      <UTMCapture />
      <VitePreloadErrorHandler />
    </div>
  </PullToReloadNative>
</template>

<style scoped>
.app-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  top: 0;
  left: 0;
  right: 0;
}

.yellow-bg-color {
  background-color: darkgoldenrod;
}

.green-bg-color {
  background-color: seagreen;
}

.violet-bg-color {
  background-color: rgb(173, 201, 214);
}

.loading-text {
  font-size: 1.5em;
  color: #333;
  font-weight: bold;
}

/* .local-environment {
  background: linear-gradient(120deg, #fda1c4 0%, #fbe9c2 100%);
} */

/* Remove the content-window class since we're using the login-screen container instead */
</style>
